import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  recommendationsApi,
  type RecommendationFilters,
  type RecommendationStatus,
} from '../api/recommendations';

const KEYS = {
  list:   (filters: object)  => ['recommendations', 'list', filters] as const,
  stats:  ()                 => ['recommendations', 'stats'] as const,
  detail: (id: number)       => ['recommendations', 'detail', id] as const,
};

export function useRecommendations(filters: RecommendationFilters = {}) {
  return useQuery({
    queryKey: KEYS.list(filters),
    queryFn:  () => recommendationsApi.list(filters),
  });
}

export function useRunRecommendations(auditRunId: number) {
  return useQuery({
    queryKey: KEYS.list({ audit_run_id: auditRunId }),
    queryFn:  () => recommendationsApi.list({ audit_run_id: auditRunId }),
    enabled:  auditRunId > 0,
  });
}

export function useRecommendation(id: number) {
  return useQuery({
    queryKey: KEYS.detail(id),
    queryFn:  () => recommendationsApi.find(id),
    enabled:  id > 0,
  });
}

export function useRecommendationStats() {
  return useQuery({
    queryKey: KEYS.stats(),
    queryFn:  () => recommendationsApi.stats(),
    staleTime: 60_000,
  });
}

export function useUpdateRecommendationStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: RecommendationStatus }) =>
      recommendationsApi.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recommendations'] });
    },
  });
}

export function useBulkUpdateStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ids, status }: { ids: number[]; status: RecommendationStatus }) =>
      recommendationsApi.bulkUpdateStatus(ids, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recommendations'] });
    },
  });
}

export function useRegenerateRecommendations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (auditRunId: number) => recommendationsApi.regenerate(auditRunId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recommendations'] });
    },
  });
}
